import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Dimensions,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../components/shared/Spinner";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import {
  mdOrderTrackingHeaders,
  nestedOrderTrackingHeaders,
  orderTrackingHeaders,
  smOrderTrackingHeaders,
} from "./utils/headers";
import { LabelBannerWithSearchBar } from "../../components/custom/Banners/LabelBannerWithSearchBar";
import {
  filterSpecificField,
  resetSearch,
  searchTrackerOrders,
  setChinaLanguage,
  sortTrackerOrdersByOrderNumber,
} from "../../redux/reducers/chinaTrackerReducer";
import { ItemTrackerBadgeHorizontalList } from "../../components/custom/Badge/ItemTrackerBadgeHorizontalList";
import { ChinaTrackerDataTableWithPagination } from "../../components/custom/DataTable/tracker/ChinaTrackerDataTableWithPagination";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import ChinaTrackerFlagModal from "./partials/ChinaTrackerFlagModal";
import ChinaTrackerIncomingOrders from "./partials/ChinaTrackerIncomingOrders";
import { getMergedTrackerOrders } from "../../redux/dispatcher/chinaTrackerDispatcher";
import { ChinaTrackerProductionLineList } from "../../components/custom/Badge/ChinaTrackerProductionLineList";
import TrackerEnquiryModal from "./partials/TrackerEnquiryModal";
import TrackerHistoryModal from "./partials/TrackerHistoryModal";
import TrackerDateModal from "./partials/TrackerDateModal";
import { translateHeaders, useTranslation } from "../../utils/useTranslation";
import { chinaTrackerTranslationMap } from "../../utils/translations";
import ChinaTrackerRejectedOrders from "./partials/ChinaTrackerRejectedOrders";
import { ConfigurationDispatcher } from "../../redux/dispatcher/configurationDispatcher";
import { actionTypesConfigurations } from "../../redux/constants/actionTypesConfigurations";
import TrackerPriorityModal from "./partials/TrackerPriorityModal";
import ChecklistModal from "./partials/ChinaChecklistModal";
import ChinaShipmentsListModal from "./partials/ChinaShipmentsListModal";
import { LabelBannerWithMultiExport } from "./utils/LabelBannerWithMultiExport";
import ChinaTrackerItemCodeInfoModal from "./partials/ChinaTrackerItemCodeInfoModal";
import ChinaTrackerRequestDateAlertsModal from "./partials/ChinaTrackerRequestDateAlertsModal ";
import ChinaTrackerInterruptRunOrder from "./partials/ChinaTrackerInterruptRunOrder";

export default function ChinaTrackerScreen({ navigation }: any) {
  const [modalOpen, setModalOpen] = useState<boolean>();
  const onClose = () => {
    setModalOpen(false);
  };
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedEnquiryType, setSelectedEnquiryType] = useState<any>(null);
  const isFocused = useIsFocused();
  const [isRefresh, setRefresh] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedProductionLine, setSelectedProductionLine] = useState<
    string | number
  >("All");
  const [expandQuickFilter, setExpandQuickFilter] = useState<boolean>(false);
  const [areFiltersExpanded, setAreFiltersExpanded] = useState(true);
  const [page, setPage] = useState<number>(0);

  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      orderTrackingHeaders
    );
  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);

  const screenWidth = Dimensions.get("window").width;
  const shouldStackUtilityCards =
    isSmallDevice || isMobileDevice || screenWidth <= 1400;
  const shouldStackFilters =
    isSmallDevice || isMobileDevice || screenWidth <= 1200;
  const shouldUseCompactControls =
    isSmallDevice || isMobileDevice || screenWidth <= 1366;

  const { t } = useTranslation();
  const columnsToNotHaveThousandsSeparator: Array<string> = ["Packing #"];
  const operationsFilterCategoryList = [
    t("all"),
    t("atRisk"),
    t("onTime"),
    t("noData"),
    t("interrupted"),
    t("delayed"),
    t("closed"),
  ];
  const productionLineFilterList = [
    "All",
    0,
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
    13,
    14,
  ];
  const [expandProductionLine, setExpandProductionLine] =
    useState<boolean>(false);

  const { userInfo, userRoles, language } = useSelector(
    (state: any) => state.configurations
  );

  const dispatch = useDispatch<any>();

  const [bellStatus, setBellStatus] = useState<boolean>(true);
  const [incomingBellStatus, setIncomingBellStatus] = useState<boolean>(true);

  const listRef = useRef<ScrollView>(null);
  const [contentOffSet, setContentOffSet] = useState<number>(0);
  const [fabPosition] = useState<number>(320);
  const handleScroll = (event: any) => {
    setContentOffSet(event.nativeEvent.contentOffset.y);
  };
  const [nestedTableHeaders, setNestedTableHeaders] = useState<
    Record<string, Record<string, string | number | boolean>>
  >(
    translateHeaders(nestedOrderTrackingHeaders, t, chinaTrackerTranslationMap)
  );
  const {
    loading,
    filteredOrders,
    sortDirection,
    approvedOrders: orders,
    incomingOrders,
    unRevisedRejectedOrders,
    isChinaTrackerLoaded,
    isLanguageSet,
  } = useSelector((state: any) => state.chinaTracker);

  const fetchTrackerOrders = () => {
    dispatch(getMergedTrackerOrders({ refresh: isRefresh }));
  };
  const navigateToOrder = (order: any, action: string) => {
    if (action === "view") {
      navigation.navigate("ChinaTrackerDetailsReport", { order });
    } else {
      setModalOpen(true);
      setSelectedItem(order);
      setSelectedEnquiryType(action);
    }
  };

  useEffect(() => {
    if (isRefresh) {
      fetchTrackerOrders();
      setRefresh(false);
      return;
    }

    if (!orders || (orders.length === 0 && !isChinaTrackerLoaded)) {
      fetchTrackerOrders();
    }
  }, [isRefresh, orders, isChinaTrackerLoaded]);

  useEffect(() => {
    if (shouldStackFilters) {
      setAreFiltersExpanded(false);
    } else {
      setAreFiltersExpanded(true);
    }
  }, [shouldStackFilters]);

  useEffect(() => {
    setTableHeaders(
      isLargeDevice
        ? translateHeaders(orderTrackingHeaders, t, chinaTrackerTranslationMap)
        : isMediumDevice
        ? translateHeaders(
            mdOrderTrackingHeaders,
            t,
            chinaTrackerTranslationMap
          )
        : translateHeaders(
            smOrderTrackingHeaders,
            t,
            chinaTrackerTranslationMap
          )
    );

    setNestedTableHeaders(
      translateHeaders(
        nestedOrderTrackingHeaders,
        t,
        chinaTrackerTranslationMap
      )
    );
  }, [isLargeDevice, isMediumDevice, language]);

  useEffect(() => {
    setBellStatus(unRevisedRejectedOrders?.length > 0);
  }, [unRevisedRejectedOrders]);

  useEffect(() => {
    if (!isLanguageSet && userInfo?.team === "china") {
      ConfigurationDispatcher(actionTypesConfigurations.Language, "zh");
      dispatch(setChinaLanguage());
    }
  });

  const handleSearch = (searchedText: string) => {
    setPage(0); // reset the page on search
    if (searchedText) {
      dispatch(searchTrackerOrders(searchedText));
    } else {
      dispatch(resetSearch({ item: "All", productionLine: "All" }));
    }
  };
  const renderFilterCard = () => (
    <View
      style={[
        styles.sectionCard,
        styles.denseCard,
        shouldUseCompactControls && styles.sectionCardCompact,
      ]}
    >
      <View style={styles.cardHeaderRow}>
        <View style={styles.headerLabelGroup}>
          <Text
            style={[
              styles.sectionTitle,
              styles.filterColumnLabel,
              shouldUseCompactControls && styles.sectionTitleCompact,
            ]}
          >
            {t("prodcutionline")}
          </Text>
          <Text
            style={[
              styles.sectionTitle,
              styles.filterColumnLabel,
              shouldUseCompactControls && styles.sectionTitleCompact,
            ]}
          >
            {t("quickFilter")}
          </Text>
        </View>
        <TouchableOpacity
          onPress={() => setAreFiltersExpanded((prev) => !prev)}
          activeOpacity={0.7}
        >
          <MaterialCommunityIcons
            name={areFiltersExpanded ? "chevron-up" : "chevron-down"}
            size={20}
            color="#111827"
          />
        </TouchableOpacity>
      </View>
      {areFiltersExpanded && (
        <View
          style={[
            styles.filterCard,
            shouldStackFilters && styles.filterCardStacked,
          ]}
        >
          <View style={styles.filterColumn}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={[
                styles.filterChipRow,
                shouldUseCompactControls && styles.filterChipRowCompact,
              ]}
            >
              <ChinaTrackerProductionLineList
                itemsList={productionLineFilterList}
                selectedCategory={selectedCategory}
                filterCategory={selectedProductionLine}
                setFilterCategory={setSelectedProductionLine!}
                tableHeaders={tableHeaders}
                trackerType="CHINA"
                expandProductionLine={expandProductionLine}
                setExpandProductionLine={setExpandProductionLine}
              />
            </ScrollView>
          </View>
          <View style={styles.filterColumn}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={[
                styles.filterChipRow,
                shouldUseCompactControls && styles.filterChipRowCompact,
              ]}
            >
              <ItemTrackerBadgeHorizontalList
                productionLine={selectedProductionLine}
                itemsList={operationsFilterCategoryList}
                filterCategory={selectedCategory || ""}
                setFilterCategory={setSelectedCategory!}
                tableHeaders={tableHeaders}
                trackerType="CHINA"
                isCollapsible
                isExpanded={expandQuickFilter}
                setIsExpanded={setExpandQuickFilter}
                collapseTriggerLabel={operationsFilterCategoryList[0]}
                collapseOnSelect
              />
            </ScrollView>
          </View>
        </View>
      )}
    </View>
  );

  const renderUtilityCards = () => {
    const canReviewRejected =
      userRoles.some((role: string) => ["ADMIN", "OWNER"].includes(role)) ||
      userInfo?.team === "purchasing";

    const alertActions: Array<{
      key: string;
      label: string;
      icon: any;
      color: string;
      textColor?: string;
      onPress?: () => void;
    }> = [
      {
        key: "request",
        label: t("requestDateAlerts"),
        icon: "bell-alert",
        color: incomingBellStatus ? "#C62828" : "#B8B8B8",
        textColor: incomingBellStatus ? "#C62828" : "#B8B8B8",
        onPress: incomingBellStatus
          ? () => navigateToOrder(orders, "request_date_alerts")
          : undefined,
      },
      {
        key: "incoming",
        label: t("incomingOrder"),
        icon: "bell",
        color: incomingBellStatus ? "#00a434" : "#B8B8B8",
        onPress: incomingBellStatus
          ? () => navigateToOrder(orders, "incoming_orders")
          : undefined,
      },
    ];

    if (canReviewRejected) {
      alertActions.splice(1, 0, {
        key: "checklist",
        label: t("checklist"),
        icon: "check",
        color: incomingBellStatus ? "#00a434" : "#B8B8B8",
        onPress: incomingBellStatus
          ? () => navigateToOrder(orders, "checklist")
          : undefined,
      });

      alertActions.splice(2, 0, {
        key: "rejected",
        label: t("reviewRejectedOrders"),
        icon: "window-close",
        color: bellStatus ? "#00a434" : "#B8B8B8",
        onPress: bellStatus
          ? () => navigateToOrder(orders, "rejected_orders")
          : undefined,
      });
    }

    return (
      <View
        style={[
          styles.utilityContainer,
          shouldStackUtilityCards && styles.utilityContainerStacked,
        ]}
      >
        <View
          style={[
            styles.sectionCard,
            styles.denseCard,
            styles.alertsCard,
            shouldUseCompactControls && styles.sectionCardCompact,
          ]}
        >
          <View style={styles.alertActionsWrapper}>
            <View style={styles.alertActionsContainer}>
              {alertActions.map((action) => (
                <TouchableOpacity
                  key={action.key}
                  style={[
                    styles.alertActionItem,
                    !action.onPress && styles.alertActionDisabled,
                  ]}
                  onPress={action.onPress}
                  activeOpacity={action.onPress ? 0.85 : 1}
                >
                  <MaterialCommunityIcons
                    name={action.icon}
                    size={16}
                    color={action.color}
                  />
                  <Text
                    style={[
                      styles.alertActionText,
                      action.textColor && { color: action.textColor },
                    ]}
                  >
                    {action.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
        <View
          style={[
            styles.sectionCard,
            styles.denseCard,
            styles.searchCard,
            shouldUseCompactControls && styles.sectionCardCompact,
          ]}
        >
          <LabelBannerWithMultiExport
            titleName={`${t("totalItems")}: ${filteredOrders.length}`}
            handleSearch={handleSearch}
            setRefresh={setRefresh}
            dataToExport={filteredOrders}
            fileName="OrderTrackerReport"
            sheetNames={["Main Orders", "Factory Details", "Shipments"]}
            t={t}
          />
        </View>
      </View>
    );
  };

  const onHandleSortColumn = (columnKey: string) => {
    if (sortDirection === "desc") {
      dispatch(sortTrackerOrdersByOrderNumber("asc"));
    } else {
      dispatch(sortTrackerOrdersByOrderNumber("desc"));
    }
  };

  const navigatePressAction = (filterField: string, filterValue: string) => {
    if (
      filterValue !== undefined &&
      filterValue !== null &&
      !(typeof filterValue === "string" && filterValue.trim() === "")
    ) {
      // dispatch here
      if (["OrderNumber1", "MSRef", "StateDelivery"].includes(filterField)) {
        dispatch(
          filterSpecificField({
            filterField: filterField,
            filterValue: filterValue,
          })
        );
      } else if (["OrderDate", "ETA"].includes(filterField)) {
        // show calender
        setModalOpen(true);
        setSelectedItem(filterValue.trim());
        setSelectedEnquiryType("date");
      }
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.topContent}>
        {renderFilterCard()}
        {renderUtilityCards()}
      </View>

      <ScrollView
        style={styles.contentContainer}
        onScroll={handleScroll}
        ref={listRef}
      >
        {loading ? (
          <View style={styles.spinnerContainer}>
            <Spinner size="small" color="#74BBFF" />
          </View>
        ) : (
          <View style={{ backgroundColor: "#fff" }}>
            <ChinaTrackerDataTableWithPagination
              isToExpandAllRows={false}
              isRowsExpandable={true}
              headers={tableHeaders}
              itemsPerPage={20}
              isNestedDataLoaded={loading}
              rows={filteredOrders}
              key="rowKeyValue"
              rowKeyValue="Key"
              columnsToNotHaveThousandSeparator={
                columnsToNotHaveThousandsSeparator
              }
              handleSortColumn={onHandleSortColumn}
              operationActions={navigateToOrder}
              itemTracker={true}
              // Nested Props
              nestedHeader={nestedTableHeaders}
              nestedItemKeyValue="details"
              // Required props
              useModal={false}
              setModalVisible={() => {}}
              handleRowClick={() => {}}
              setSelected={() => {}}
              handleDeleteRow={() => {}}
              navigatePressAction={navigatePressAction}
              t={t}
              page={page}
              setPage={setPage}
            />
          </View>
        )}
      </ScrollView>

      {modalOpen && selectedEnquiryType === "flag" && (
        <ChinaTrackerFlagModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "interrupt" && (
        <ChinaTrackerInterruptRunOrder
          onClose={onClose}
          visible={modalOpen}
          runOrder={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "incoming_orders" && (
        <ChinaTrackerIncomingOrders
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "request_date_alerts" && (
        <ChinaTrackerRequestDateAlertsModal
          onClose={onClose}
          visible={modalOpen}
        />
      )}

      {modalOpen && selectedEnquiryType === "rejected_orders" && (
        <ChinaTrackerRejectedOrders
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "checklist" && (
        <ChecklistModal onClose={onClose} visible={modalOpen} />
      )}

      {modalOpen && selectedEnquiryType === "add" && (
        <TrackerEnquiryModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "history" && (
        <TrackerHistoryModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "shipments" && (
        <ChinaShipmentsListModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "date" && (
        <TrackerDateModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "priority" && (
        <TrackerPriorityModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "itemCodeReport" && (
        <ChinaTrackerItemCodeInfoModal
          onClose={onClose}
          visible={modalOpen}
          itemCode={selectedItem}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F4F6F8",
  },
  pageScroll: {
    flex: 1,
  },
  pageScrollContent: {
    padding: 16,
    paddingBottom: 32,
    gap: 16,
  },
  topContent: {
    gap: 12,
    marginBottom: 10,
  },
  sectionCard: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 12,
    shadowColor: "rgba(15, 23, 42, 0.08)",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 12,
    elevation: 2,
  },
  denseCard: {
    paddingVertical: 10,
    paddingHorizontal: 10,
  },
  sectionCardCompact: {
    padding: 8,
    borderRadius: 10,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: "#111827",
    marginBottom: 6,
  },
  sectionTitleCompact: {
    fontSize: 12,
    marginBottom: 4,
  },
  filterChipRow: {
    paddingRight: 8,
    paddingBottom: 2,
  },
  filterChipRowCompact: {
    paddingBottom: 0,
  },
  filterCard: {
    flexDirection: "row",
    gap: 12,
  },
  filterCardStacked: {
    flexDirection: "column",
  },
  cardHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  headerLabelGroup: {
    flex: 1,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  filterColumnLabel: {
    flex: 1,
  },
  filterColumn: {
    flex: 1,
  },
  utilityContainer: {
    flexDirection: "row",
    gap: 16,
  },
  utilityContainerStacked: {
    flexDirection: "column",
  },
  alertsCard: {
    flex: 1,
  },
  alertActionsWrapper: {
    minHeight: 64,
    justifyContent: "center",
  },
  alertActionsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "flex-start",
    alignItems: "center",
  },

  alertActionItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 999,
    backgroundColor: "#F1F5F9",
  },
  alertActionDisabled: {
    opacity: 0.5,
  },
  alertActionText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#111827",
  },
  searchCard: {
    flex: 1,
    justifyContent: "center",
  },
  tableCard: {
    padding: 0,
    overflow: "hidden",
  },
  spinnerCard: {
    alignItems: "center",
    justifyContent: "center",
    minHeight: 200,
  },
  contentContainer: {
    flex: 1,
  },
  spinnerContainer: {
    position: "absolute",
    top: 100,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
  },
});
